import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { MapPin, MapPinOff } from "lucide-react";
import { useLocationPermission } from "@/hooks/use-location-permission";
import { isFavoritesPath } from "@/features/stops/favorites-route";
import { LocationSettingsSheet } from "./location-settings-sheet";

/**
 * Converts a Unix timestamp to elapsed minutes relative to now.
 * Defined at module level (not inside render) to satisfy the ESLint rule that
 * forbids bare `Date.now()` calls inside component bodies.
 */
function minutesAgoFromTimestamp(timestamp: number): number {
  return Math.floor((Date.now() - timestamp) / 60_000);
}

/**
 * Persistent location status bar rendered at the top of the app shell.
 *
 * Lives in document flow (not `position: fixed`) so it naturally pushes
 * page content down without requiring padding adjustments on child pages.
 *
 * **Visibility rules:**
 * - Hidden until `hasCompletedFlow` is true (first-time users see nothing).
 * - Hidden while `status === "verifying"` (probe in flight, no bar yet).
 * - Suppressed on the exact `/nearby` route, where the location prompt page
 *   manages its own denied-state UI.
 * - Suppressed on `/favorites` and `/favorites/:id/arrivals`: Favorites never
 *   need location, so the bar would only be noise there.
 *
 * **Visual states:**
 * - *Granted*: green tint, pulsing dot, "Location active".
 * - *Unavailable*: amber tint, MapPin icon, "Searching for location…". Non-interactive —
 *   the watcher is still running and will recover to granted when signal returns.
 * - *Denied/off*: neutral background, MapPinOff icon, "Location off — tap to manage".
 *
 * Tapping the bar opens {@link LocationSettingsSheet} as an inline bottom sheet.
 * The `minutesAgo` value is snapshotted at open time (not during render) to
 * avoid triggering the `no-Date.now-in-render` lint rule.
 * `positionTimestamp` is tracked locally via a `useEffect` that fires whenever
 * `position` changes — the hook no longer persists it.
 */
export function LocationStatusBar() {
  const { status, hasCompletedFlow, position, resetPermissionState } =
    useLocationPermission();
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [minutesAgo, setMinutesAgo] = useState<number | null>(null);
  const positionTimestampRef = useRef<number | null>(null);

  // Snapshot the time whenever watchPosition delivers a fresh position.
  // useEffect is the correct place for Date.now() — not render.
  useEffect(() => {
    if (position !== null) {
      positionTimestampRef.current = Date.now();
    }
  }, [position]);

  if (!hasCompletedFlow) return null;
  if (status === "verifying") return null;
  if (location.pathname === "/nearby") return null;
  if (isFavoritesPath(location.pathname)) return null;

  const isGranted = status === "granted";

  const openSettings = () => {
    setMinutesAgo(
      positionTimestampRef.current != null
        ? minutesAgoFromTimestamp(positionTimestampRef.current)
        : null,
    );
    setSettingsOpen(true);
  };

  const disableLocation = () => {
    resetPermissionState();
    setSettingsOpen(false);
    void navigate("/nearby", { replace: true });
  };

  if (status === "unavailable") {
    return (
      <div className="w-full flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-800 text-sm">
        <MapPin size={14} />
        <span>Searching for location…</span>
      </div>
    );
  }

  return (
    <>
      <button
        aria-label="Manage location settings"
        onClick={openSettings}
        className={
          isGranted
            ? "w-full flex items-center gap-2 px-4 py-2 bg-green-50 text-green-800 text-sm"
            : "w-full flex items-center gap-2 px-4 py-2 bg-zinc-100 text-zinc-700 text-sm"
        }
      >
        {isGranted ? (
          <>
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="flex-1 text-left">Location active</span>
            <span className="text-xs text-green-600">Manage ›</span>
          </>
        ) : (
          <>
            <MapPinOff size={14} />
            <span className="flex-1 text-left">
              Location off — tap to manage
            </span>
          </>
        )}
      </button>

      {settingsOpen && (
        <LocationSettingsSheet
          isGranted={isGranted}
          minutesAgo={minutesAgo}
          onDisable={disableLocation}
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
      )}
    </>
  );
}
